import PropTypes from "prop-types";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Area,
  ReferenceLine 
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    const formattedValue = value >= 1000000
      ? `₦${value/1000000}M`
      : value >= 1000
      ? `₦${value/1000}K`
      : `₦${value}`;

    return (
      <div className="bg-white p-2 border border-gray-200 rounded-md shadow-md">
        <p className="text-sm">{`Time: ${label}`}</p>
        <p className="text-sm font-bold">{`Value: ${formattedValue}`}</p>
      </div>
    );
  }
  return null;
};

const Chart = ({ 
  data, 
  type = 'Income', // or 'Expense'
  height = 300,
  minWidth = 300
}) => {
  const lineColor = type === 'Income' ? '#22C55E' : '#EF4444';
  
  // Calculate maximum value for dynamic Y-axis scaling
  const maxValue = Math.max(...data.map(item => item.value), 1000);
  
  const calculateTicks = () => {
    const tickCount = 5;
    const step = Math.ceil(maxValue / tickCount);
    const roundedStep = Math.ceil(step / 1000) * 1000; // Round to nearest thousand
    
    let ticks = [];
    for (let i = 0; i <= tickCount; i++) {
      ticks.push(i * roundedStep);
    }
    
    return ticks;
  };
  
  const yAxisTicks = calculateTicks();
  const yAxisDomain = [0, yAxisTicks[yAxisTicks.length - 1]];

  return (
    <div className="bg-white p-6 rounded-xl w-full">
      <ResponsiveContainer width="100%" height={height} minWidth={minWidth}>
        <LineChart 
          data={data} 
          margin={{ top: 20, right: 10, left: -20, bottom: 20 }}
        >
          <CartesianGrid 
            horizontal={true}
            vertical={false} 
            stroke="#E5E7EB" 
          />
          <XAxis 
            dataKey="time" 
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#4B5563', fontSize: 12 }}
            padding={{ left: 0, right: 0 }}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#4B5563', fontSize: 12 }}
            tickFormatter={(value) => 
              value >= 1000000 
                ? `${(value/1000000).toFixed(1)}M` 
                : `${(value/1000).toFixed(0)}K`
            }
            ticks={yAxisTicks}
            domain={yAxisDomain}
            padding={{ top: 20, bottom: 20 }}
          />
          <ReferenceLine 
            x="12:00"
            stroke="#E5E7EB"
            strokeWidth={1}
          />
          <Tooltip content={<CustomTooltip />} cursor={false} />
          <Line
            type="monotone"
            dataKey="value"
            stroke={lineColor}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6, fill: lineColor }}
          />
          <defs>
            <linearGradient id={`color${type}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={lineColor} stopOpacity={0.1}/>
              <stop offset="95%" stopColor={lineColor} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="value"
            stroke="false"
            fillOpacity={1}
            fill={`url(#color${type})`}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

Chart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      time: PropTypes.string,
      value: PropTypes.number,
    })
  ),
  type: PropTypes.string,
  height: PropTypes.number,
  minWidth: PropTypes.number,
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
    })
  ),
  label: PropTypes.string,
};

export default Chart;